/**
 * Offline answer queue + Background Sync integration.
 *
 * When the network is down, answer submissions are queued in IndexedDB and
 * automatically replayed via:
 *   1. Background Sync API (`navigator.serviceWorker.ready.sync.register`)
 *   2. Fallback: `online` event listener (Safari has no Sync API)
 *
 * Used by Exam Mode (`/api/student/answer`) and Smart Review
 * (`/api/student/smart-review/answer`).
 */
import { useEffect, useState } from "react";

const DB_NAME = "prepmasters-offline";
const DB_VERSION = 1;
const STORE = "answer_queue";
const SYNC_TAG = "pm-answer-sync";

function openDB() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    Promise.resolve(fn(store))
      .then((r) => { result = r; })
      .catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function enqueueAnswer({ endpoint, payload, token }) {
  return withStore("readwrite", (store) => new Promise((res, rej) => {
    const req = store.add({
      endpoint,
      payload,
      token,
      queued_at: new Date().toISOString(),
    });
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  }));
}

export async function getPendingCount() {
  try {
    return await withStore("readonly", (store) => new Promise((res, rej) => {
      const req = store.count();
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    }));
  } catch { return 0; }
}

export async function listPending() {
  try {
    return await withStore("readonly", (store) => new Promise((res, rej) => {
      const req = store.getAll();
      req.onsuccess = () => res(req.result || []);
      req.onerror = () => rej(req.error);
    }));
  } catch { return []; }
}

async function deleteEntry(id) {
  return withStore("readwrite", (store) => new Promise((res, rej) => {
    const req = store.delete(id);
    req.onsuccess = () => res();
    req.onerror = () => rej(req.error);
  }));
}

/**
 * Drain the queue from the main thread. Returns { synced, failed }.
 * The service worker `sync` handler does the same drain, so this is the
 * fallback path for browsers without Background Sync.
 */
export async function flushQueue(baseUrl) {
  const entries = await listPending();
  let synced = 0, failed = 0;
  for (const e of entries) {
    try {
      const res = await fetch(`${baseUrl}${e.endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${e.token}`,
        },
        body: JSON.stringify(e.payload),
      });
      if (res.ok || res.status === 400 || res.status === 404) {
        // Drop on success OR on hard 4xx (won't succeed on retry — e.g. deleted question)
        await deleteEntry(e.id);
        if (res.ok) synced += 1;
        else failed += 1;
      } else {
        // 5xx or network — leave queued for the next attempt
        failed += 1;
      }
    } catch {
      failed += 1;
    }
  }
  return { synced, failed };
}

export async function registerBackgroundSync() {
  if (!("serviceWorker" in navigator)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    if ("sync" in reg) {
      await reg.sync.register(SYNC_TAG);
      return true;
    }
  } catch { /* noop */ }
  return false;
}

/**
 * React hook — returns { pending, synced, isOnline, flushNow }.
 * Auto-flushes on `online` event (covers browsers without Background Sync).
 * Also listens for service worker messages so SW-side drains update the count.
 */
export function useOfflineQueue(baseUrl) {
  const [pending, setPending] = useState(0);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );

  const refresh = async () => setPending(await getPendingCount());

  const flushNow = async () => {
    if (!baseUrl) return;
    const r = await flushQueue(baseUrl);
    await refresh();
    return r;
  };

  useEffect(() => {
    refresh();
    const onOnline = async () => {
      setIsOnline(true);
      // Try SW sync first; fall back to direct drain if not available.
      const ok = await registerBackgroundSync();
      if (!ok) await flushNow();
      else setTimeout(refresh, 2000);
    };
    const onOffline = () => setIsOnline(false);
    const onSWMsg = (e) => {
      if (e.data && e.data.type === "pm-sync-done") refresh();
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSWMsg);
    }
    const t = setInterval(refresh, 10000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSWMsg);
      }
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  return { pending, isOnline, flushNow, refresh };
}

/**
 * Wrap an axios POST so that on network failure we queue offline and tell
 * the caller the result was queued. Returns { ok, queued, data, error }.
 *
 * For answer-submission endpoints, automatically stamps `answered_at` into the
 * payload so the backend can preserve the actual answer time during sync —
 * protects the student's daily streak when answering offline. (Backend
 * sanitizes timestamps against future-dating + 14-day staleness.)
 */
const ANSWER_ENDPOINTS = new Set(["/student/answer", "/student/smart-review/answer"]);

export async function postOrQueue(api, endpoint, payload) {
  const stamped = ANSWER_ENDPOINTS.has(endpoint) && !payload.answered_at
    ? { ...payload, answered_at: new Date().toISOString() }
    : payload;
  try {
    const r = await api.post(endpoint, stamped);
    return { ok: true, queued: false, data: r.data };
  } catch (e) {
    const isNetwork = !e?.response;  // axios sets `response` only when server replied
    if (isNetwork && navigator.onLine === false) {
      const token = localStorage.getItem("pm_token") || "";
      await enqueueAnswer({ endpoint, payload: stamped, token });
      await registerBackgroundSync();
      return { ok: false, queued: true };
    }
    return { ok: false, queued: false, error: e };
  }
}
